import AttachCloudDetails, { AttachCloudDetailsCloudConfig } from "components/AttachCloud/Details";
import CollapsiblePanel from "components/CollapsiblePanel";
import CollapsiblePanelContent from "components/CollapsiblePanel/Content";
import CollapsiblePanelHeader from "components/CollapsiblePanel/Header";
import CollapsiblePanelTitle from "components/CollapsiblePanel/Title";
import CollapsiblePanelToggleTrigger from "components/CollapsiblePanel/ToggleTrigger";
import { EmptystateLinkColored, EmptystateMagnetColored } from "components/icons/generated";
import Box from "ds/components/Box";
import Counter from "ds/components/Counter";
import EmptyState from "ds/components/EmptyState";
import { useToggle } from "hooks/useToggle";
import { CLOUD_INTEGRATIONS, CloudIntegrationAttachment } from "shared/CloudIntegration/types";
import { StackAwsIntegrationAttachment } from "types/generated";

import styles from "./styles.module.css";

type AttachedCloudIntegrationsSectionProps = {
  title: string;
  integrations: Array<CloudIntegrationAttachment>;
  cloudConfig: AttachCloudDetailsCloudConfig;
  stackId: string;
  readOnly: boolean;
  onSuccessDetach?: (type: CLOUD_INTEGRATIONS) => void;
  isAutoAttached?: boolean;
  stackLabels?: string[];
  refetchQueries?: string[];
};

const getIntegrationType = (integration: CloudIntegrationAttachment) => {
  if (integration.__typename === "StackAzureIntegrationAttachment") {
    return CLOUD_INTEGRATIONS.Azure;
  } else if (integration.__typename === "StackAwsIntegrationAttachment") {
    return CLOUD_INTEGRATIONS.AWS;
  } else if (integration.__typename === "StackIntegrationGcp") {
    return CLOUD_INTEGRATIONS.GCP;
  }
  return CLOUD_INTEGRATIONS.AWS;
};

const AttachedCloudIntegrationsSection = ({
  title,
  integrations,
  cloudConfig,
  stackId,
  readOnly,
  onSuccessDetach,
  isAutoAttached,
  stackLabels,
  refetchQueries,
}: AttachedCloudIntegrationsSectionProps) => {
  const [isCollapsed, handleToggle] = useToggle(integrations.length === 0);

  return (
    <CollapsiblePanel
      variant="section"
      isCollapsed={isCollapsed}
      onToggle={handleToggle}
      withTransition
      noBorder
    >
      <CollapsiblePanelHeader justify="between" padding="x-large" align="center">
        <Box grow="1" align="center" gap="large">
          <CollapsiblePanelToggleTrigger ariaLevel={2} ariaLabel={title} />
          <CollapsiblePanelTitle>{title}</CollapsiblePanelTitle>
          <Counter count={integrations.length} size="small" />
        </Box>
      </CollapsiblePanelHeader>
      <CollapsiblePanelContent padding="0 x-large x-large x-large" gap="large">
        {integrations.map((integration, index) => {
          const integrationType = getIntegrationType(integration);
          return (
            <AttachCloudDetails
              key={
                (integration as StackAwsIntegrationAttachment).id ?? `${integrationType}-${index}`
              }
              cloudConfig={cloudConfig}
              attachedIntegrationType={integrationType}
              attachedIntegration={integration}
              entityId={stackId}
              refetchQueries={refetchQueries}
              readOnly={readOnly}
              onSuccessDetach={() => onSuccessDetach?.(integrationType)}
              stackLabels={stackLabels}
            />
          );
        })}
        {integrations.length === 0 && (
          <EmptyState
            className={styles.emptyState}
            icon={isAutoAttached ? EmptystateMagnetColored : EmptystateLinkColored}
            title={
              isAutoAttached ? "No auto-attached integrations yet" : "No attached integrations yet"
            }
            caption={
              isAutoAttached
                ? "This is where auto-attached integrations will appear. Use autoattach label to attach integrations to this resource."
                : "This is where integrations that you have directly attached to this resource will appear."
            }
          />
        )}
      </CollapsiblePanelContent>
    </CollapsiblePanel>
  );
};

export default AttachedCloudIntegrationsSection;
